import React, { ComponentType, useContext, useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Typography,
  Box,
  IconButton,
  Stack,
  Slider,
  Alert,
  Grid,
  TextField,
  InputBaseComponentProps,
  Divider,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import PaymentIcon from '@mui/icons-material/Payment';
import { bold } from '@styles/fontWeights';
import { useCreateTeamMessage } from '../hooks/useCreateTeamMessage';
import { authContext } from '@staffedupusa/shared-components';
import { toast } from 'react-toastify';
import { LoadingButton } from '@mui/lab';
import { useGetPaymantDetails } from '@features/payments/hooks/useGetPaymantDetails';
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import StripeInput from '@features/employerOnboarding/components/StripeInput/StripeInput';
import { StripeCardElementChangeEvent } from '@stripe/stripe-js';
interface PurchaseDialogProps {
  open: boolean;
  handleClose: () => void;
  onSave?: (selectedValue: string) => void;
}

const TEXTPPRICEFOR = 0.05;

const actualValues = [
  500, 1000, 1500, 2000, 3000, 4000, 5000, 7000, 8000, 10000,
];

const packageSteps = actualValues.map((amount, index) => {
  const price = amount * TEXTPPRICEFOR;
  return {
    value: index + 1,
    price,
    label: `${amount} for $${price}`,
    actualValue: String(amount),
  };
});

const PurchaseDialog: React.FC<PurchaseDialogProps> = ({
  open,
  handleClose,
  onSave,
}) => {
  const { authData } = useContext(authContext);
  const stripe = useStripe();
  const elements = useElements();
  const allocatedTexts =
    authData?.user?.company?.additional_info?.allocated_texts;
  const [errorText, setErrorText] = useState('');
  const [sliderValue, setSliderValue] = useState<number>(1);
  const [hasPaymentMethod, setHasPaymentMethod] = useState(false);
  const [cardComplete, setCardComplete] = React.useState({
    number: false,
    expiry: false,
    cvc: false,
  });

  const { paymentInfo } = useGetPaymantDetails();

  const { createTeamMessage, isCreatingTeamMessage } = useCreateTeamMessage({
    setErrorText,
  });
  const selectedPackage = packageSteps.find(
    (step) => step.value === sliderValue,
  );
  const handleCardChange = (
    field: 'number' | 'expiry' | 'cvc',
    event: StripeCardElementChangeEvent,
  ) => {
    setCardComplete((prev) => ({ ...prev, [field]: event.complete }));
  };
  const handleSave = async () => {
    if ((!stripe || !elements) && !hasPaymentMethod) {
      return;
    }
    const options = {
      onSuccess: (data: { status: number; message: string }) => {
        if (data && data?.status === 200) {
          toast.success('Text credits purchase successfully');
          setErrorText('');
          handleClose();
        } else {
          setErrorText(data.message);
        }
      },
    };
    const payload: {
      extra_text: boolean;
      message_status: boolean;
      allocated_texts: any;
      amount: number | undefined;
      token?: string;
    } = {
      extra_text: true,
      message_status: true,
      allocated_texts:
        Number(selectedPackage?.actualValue) + (allocatedTexts || 0),
      amount: selectedPackage?.price,
    };
    if (!hasPaymentMethod && stripe && elements) {
      const cardElement = elements.getElement(CardNumberElement);
      if (cardElement) {
        const { token } = await stripe.createToken(cardElement);
        payload.token = token?.id;
      }
    }
    createTeamMessage(payload, options);
  };
  useEffect(() => {
    if (
      paymentInfo &&
      paymentInfo?.object &&
      paymentInfo?.object?.data?.length > 0
    ) {
      setHasPaymentMethod(true);
    } else {
      setHasPaymentMethod(false);
    }
  }, [paymentInfo]);
  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs">
      <DialogTitle sx={{ pb: 0 }}>
        <IconButton
          sx={{ position: 'absolute', top: 5, right: 5 }}
          onClick={handleClose}
        >
          <Close />
        </IconButton>
        <PaymentIcon
          sx={{
            fontSize: 60,
            color: 'primary.main',
            margin: 'auto !important',
          }}
        />
        <Typography
          variant="h4"
          component="p"
          color="secondary.light"
          p={2}
          fontWeight={bold}
        >
          Purchase text credits now
        </Typography>
      </DialogTitle>

      <DialogContent>
        {errorText && <Alert severity="error">{errorText}</Alert>}
        <Typography
          textAlign="center"
          variant="subtitle2"
          color="text.secondary"
          gutterBottom
        >
          Select from the following text message credit packages:
        </Typography>

        <Box mt={4} px={2}>
          <Slider
            value={sliderValue}
            min={1}
            max={10}
            step={1}
            marks={packageSteps.map((step) => ({
              value: step.value,
              label: '',
            }))}
            onChange={(_, val) => setSliderValue(val as number)}
            valueLabelDisplay="off"
          />
          <Typography
            textAlign="center"
            mt={2}
            variant="h6"
            fontWeight="bold"
            color="primary"
          >
            {selectedPackage
              ? `Purchase ${Number(selectedPackage.actualValue).toLocaleString()} text messages for $${selectedPackage.price}`
              : ''}
          </Typography>
        </Box>
        {!hasPaymentMethod && (
          <>
            <Divider sx={{ width: '100%', mt: 2 }} />
            <Typography variant="h6" textAlign="center" sx={{ mt: 2 }}>
              Add Payment Details
            </Typography>
            <Grid container spacing={2} rowGap={1} sx={{ mt: 2 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  InputProps={{
                    inputComponent:
                      StripeInput as ComponentType<InputBaseComponentProps>,
                    inputProps: {
                      component: CardNumberElement,
                      onChange: (e: any) => handleCardChange('number', e),
                    },
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  size="small"
                  variant="outlined"
                  InputProps={{
                    inputComponent:
                      StripeInput as ComponentType<InputBaseComponentProps>,
                    inputProps: {
                      component: CardExpiryElement,
                      onChange: (e: any) => handleCardChange('expiry', e),
                    },
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  size="small"
                  InputProps={{
                    inputComponent:
                      StripeInput as ComponentType<InputBaseComponentProps>,
                    inputProps: {
                      component: CardCvcElement,
                      onChange: (e: any) => handleCardChange('cvc', e),
                    },
                  }}
                />
              </Grid>
            </Grid>
          </>
        )}
        <Stack direction="column" spacing={2} mt={4} mb={2} alignItems="center">
          <LoadingButton
            loading={isCreatingTeamMessage}
            onClick={handleSave}
            disabled={
              !hasPaymentMethod &&
              (!cardComplete.number ||
                !cardComplete.expiry ||
                !cardComplete.cvc)
            }
            variant="contained"
            sx={{ width: '100%' }}
          >
            Purchase {selectedPackage ? selectedPackage.actualValue : '0'} Texts
            credits
          </LoadingButton>
          <Button
            disabled={isCreatingTeamMessage}
            variant="outlined"
            sx={{ width: '100%' }}
            onClick={handleClose}
          >
            Cancel
          </Button>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default PurchaseDialog;
